<script setup>
import { ref } from 'vue';
import { useStore } from 'vuex';
import { joinPath, checkForFileExtension, parseFolder } from '@/utils/file';
import { forEachAsync, concurrentForEachAsync } from '@/utils/array';
import {
  parseMaterial,
  parseTexture,
  parseTextureFile,
  removeTexturesFromModel,
  filterFilesIncludedInGLTF,
} from '@/utils/gltf';
import { uploadResource } from '../connectors/ras';

const store = useStore();

const filesSelected = ref([]);
const uploadInProgress = ref(false);
const progressStatus = ref('');

/**
 * Function to handle click event on upload
 */
const uploadFiles = async () => {
  uploadInProgress.value = true;
  progressStatus.value = 'Upload is starting';

  try {
    const modelFileObj = filesSelected.value.find((file) => checkForFileExtension(file.name, 'gltf'));
    const modelDir = modelFileObj.fullPath.replace(modelFileObj.name, '');

    const modelFile = await new Promise((resolve, reject) => modelFileObj.file(resolve, reject));

    const model = await new Promise((resolve) => {
      const reader = new FileReader();
      reader.readAsText(modelFile);
      reader.onload = (evt) => resolve(JSON.parse(evt.target.result));
    });

    const textureFiles = Array(model.images?.length);

    let processedTextures = 0;
    await concurrentForEachAsync(model.images || [], async (image, index) => {
      const textureFile = await store.dispatch('textureFiles/createTextureFile', parseTextureFile(image));

      await forEachAsync(textureFile.files, async ({ name: quality, url }) => {
        const textureFilePath = joinPath(modelDir, image.uri);
        const qualityFilePath = joinPath(modelDir, image.uri.replace(/(initial|final_desktop|final_mobile)/, quality));
        const textureImage =
          filesSelected.value.find((file) => file.fullPath === qualityFilePath) ||
          filesSelected.value.find((file) => file.fullPath === textureFilePath);

        const file = await new Promise((resolve, reject) => textureImage.file(resolve, reject));

        await uploadResource(url, file);
      });

      textureFiles[index] = textureFile;

      processedTextures += 1;
      progressStatus.value = `Processed textures ${processedTextures} of ${model.images.length}`;
    });

    let processedMaterials = 0;
    await concurrentForEachAsync(model.materials || [], async (rawMaterial) => {
      const material = await parseMaterial(rawMaterial, {
        processChannel: async ({ channel, channelsTexture: { extensions, texCoord, index: textureIndex } }) => {
          try {
            const rawTexture = model.textures[textureIndex];
            const texture = parseTexture(
              {
                name: `${rawMaterial.name}.${channel}`,
                textureFile: textureFiles[rawTexture.source].shortId,
              },
              rawTexture,
              { model, extensions, texCoord },
            );

            return { channel, name: texture.name, data: texture.data };
          } catch (err) {
            console.error(rawMaterial, { channel, channelsTexture: { extensions, texCoord, index: textureIndex } });
            throw err;
          }
        },
      });

      await store.dispatch('materials/createMaterial', material);

      processedMaterials += 1;

      progressStatus.value = `Processed materials ${processedMaterials} of ${model.materials.length}`;
    });

    const {
      files: [{ url: modelS3Url }, ...bufferFiles],
    } = await store.dispatch('models/createModel', {
      name: modelFileObj.name,
      files: [{ name: modelFileObj.name }, ...(model.buffers || []).map(({ uri }) => ({ name: uri }))],
    });

    await fetch(modelS3Url, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/octet-stream', maxBodyLength: Infinity },
      body: JSON.stringify(removeTexturesFromModel(model)),
    });
    progressStatus.value = 'Processed Model File';
    await forEachAsync(bufferFiles, async ({ url }, index) => {
      const bufferBin = filesSelected.value.find(
        (file) => file.fullPath === joinPath(modelDir, model.buffers[index].uri),
      );
      await fetch(url, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/octet-stream', maxBodyLength: Infinity },
        body: await new Promise((resolve, reject) => bufferBin.file(resolve, reject)),
      });
      progressStatus.value = `Processed Buffers ${index + 1} of ${bufferFiles.length}`;
    });

    progressStatus.value = 'Success';
  } catch (err) {
    console.error(err);
    progressStatus.value = `${err}`;
  } finally {
    await new Promise((resolve) => setTimeout(resolve, 2000));

    uploadInProgress.value = false;
    filesSelected.value.length = 0;
    progressStatus.value = '';
  }
};

/**
 * Function to handle the drop event on canvas
 */
const dropFiles = async (e) => {
  try {
    filesSelected.value.length = 0;

    const allFiles = [];

    await [...e.dataTransfer.items].reduce(async (acc, item) => {
      const entry = item.getAsEntry ? item.getAsEntry() : item.webkitGetAsEntry();
      if (entry.isFile) {
        allFiles.push(entry);
      } else {
        allFiles.push(...(await parseFolder(entry)));
      }
      await acc;
    }, Promise.resolve());

    // Find the first model file
    const modelFile = allFiles.find(({ name }) => checkForFileExtension(name, 'gltf'));

    if (!modelFile) {
      filesSelected.value.length = 0;
      return;
    }

    const file = await new Promise((resolve) => modelFile.file(resolve));

    // read GLTF file to get bin and image paths
    await new Promise((resolve) => {
      const reader = new FileReader();
      reader.readAsText(file);
      reader.onload = (evt) => {
        const { buffers = [], images = [] } = JSON.parse(evt.target.result);

        filesSelected.value = filterFilesIncludedInGLTF(allFiles, {
          buffers,
          images,
          modelFile,
        });

        resolve();
      };
    });
  } catch (err) {
    console.error(`Drop failed: ${err}`);
  }
};
</script>

<template>
  <div id="upload" class="relative my-6 mx-auto flex p-10">
    <div
      class="
        relative
        flex
        h-96
        w-96
        flex-col
        items-center
        justify-center
        rounded-2xl
        border-4 border-dashed border-gray-200
        text-gray-500
        shadow-inner
      "
    >
      <canvas
        id="upload__dropzone"
        class="absolute top-0 left-0 block h-full w-full rounded-2xl"
        @dragover.prevent=""
        @drop.prevent="dropFiles"
      />
      <h4>Drop asset files</h4>
    </div>
    <div v-show="filesSelected.length" id="upload__file-listing" class="flex-col px-8">
      <ul class="mb-6 flex flex-col max-h-96 overflow-y-auto">
        <li v-for="file in filesSelected" :key="file.name" class="mb-1 flex flex-row border-gray-400">
          <div
            class="
              flex flex-1
              transform
              cursor-pointer
              select-none
              items-center
              rounded-md
              bg-gray-200
              p-2
              transition
              duration-500
              ease-in-out
              hover:-translate-y-1 hover:shadow-lg
            "
          >
            <div class="mr-4 flex h-10 w-10 flex-col items-center justify-center rounded-md bg-gray-300 text-xs">
              {{ file.name.split('.').pop() }}
            </div>
            <div class="mr-16 flex-1 pl-1">
              <div class="text-sm font-medium">{{ file.name }}</div>
              <div class="text-xs text-gray-600">
                {{ file.fullPath }}
              </div>
            </div>
          </div>
        </li>
      </ul>
      <div class="flex justify-center">
        <button
          id="upload__submit"
          :disabled="uploadInProgress"
          class="
            flex
            place-self-center
            self-center
            justify-self-center
            rounded-full
            border
            bg-red-400
            px-4
            py-2
            text-sm text-white
            hover:bg-red-700
            disabled:cursor-not-allowed disabled:opacity-25
          "
          @click="uploadFiles"
        >
          Upload
        </button>
      </div>
    </div>
    <div
      v-show="progressStatus"
      id="upload__progress"
      class="absolute top-0 left-0 flex h-full w-full items-center justify-center bg-gray-100 text-brand-900 opacity-75"
    >
      <svg
        v-if="progressStatus !== 'Success'"
        fill="none"
        class="h-6 w-6 animate-spin"
        viewBox="0 0 32 32"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          clip-rule="evenodd"
          d="M15.165 8.53a.5.5 0 01-.404.58A7 7 0 1023 16a.5.5 0 011 0 8 8 0 11-9.416-7.874.5.5 0 01.58.404z"
          fill="currentColor"
          fill-rule="evenodd"
        />
      </svg>
      {{ progressStatus }}
    </div>
  </div>
</template>
